import React, { useState, useEffect } from 'react';
import api from './api';

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [editingId, setEditingId] = useState(null);

    const [form, setForm] = useState({
        username: "",
        password: "",
        fullName: "",
        isActive: true,
        homepage: "/", // Default
        access: { office: true, factory: false },
        permissions: {
            roughModule: false, mfgModule: false, factoryModule: false, galaxyModule: false,
            salesModule: false, financeModule: false, masterModule: false, userManageModule: false
        }
    });

    // --- LIST OF ALL AVAILABLE PAGES ---
    const APP_PAGES = [
        { label: "🏠 Admin Dashboard", value: "/" },
        { label: "🏭 Labor Kiosk (Factory)", value: "/labor" },
        { label: "⚙️ Auto Dept (Machines)", value: "/auto" },
        { label: "🌌 Galaxy Dept Kiosk", value: "/galaxy-job?mode=kiosk" },
        { label: "➕ Rough Purchase", value: "/inward" },
        { label: "💰 Sales Department", value: "/sell" },
        { label: "Cut Stock", value: "/cuts" },
        { label: "📦 Dispatch / Jangad", value: "/jangad" },
        { label: "💵 Finance / Accounts", value: "/padtar-calculator" },
    ];

    useEffect(() => { loadUsers(); }, []);

    const loadUsers = async () => {
        try { const res = await api.get('/auth/list'); setUsers(res.data); } 
        catch (err) { console.error(err); }
    };

    const resetForm = () => {
        setEditingId(null);
        setForm({
            username: "", password: "", fullName: "", isActive: true, homepage: "/",
            access: { office: true, factory: false },
            permissions: { 
                roughModule: false, mfgModule: false, factoryModule: false, galaxyModule: false, 
                salesModule: false, financeModule: false, masterModule: false, userManageModule: false 
            }
        });
    };

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
    
    const toggleAccess = (field) => setForm({ ...form, access: { ...form.access, [field]: !form.access[field] } });
    
    const togglePerm = (field) => setForm({ ...form, permissions: { ...form.permissions, [field]: !form.permissions[field] } });

    const handleEdit = (user) => {
        setEditingId(user._id);
        setForm({
            username: user.username,
            password: "",
            fullName: user.fullName,
            isActive: user.isActive,
            homepage: user.homepage || "/",
            access: { 
                office: user.canAccessOfficeApp, 
                factory: user.canAccessFactoryApp 
            },
            permissions: user.permissions
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingId) {
                await api.put(`/auth/update/${editingId}`, form);
                alert("✅ User Updated!");
            } else {
                await api.post('/auth/register', form);
                alert("✅ User Created!");
            }
            resetForm();
            loadUsers();
        } catch (err) {
            alert("Error: " + (err.response?.data?.message || err.message));
        }
    };

    return (
        <div className="container mt-4">
            <h3 className="mb-4 fw-bold text-primary">👤 User & Permission Manager</h3>

            <div className="row">
                <div className="col-lg-5 mb-4">
                    <div className={`card shadow border-${editingId ? 'warning' : 'primary'}`}>
                        <div className={`card-header text-white fw-bold ${editingId ? 'bg-warning text-dark' : 'bg-primary'}`}>
                            {editingId ? '✏️ Editing User' : '➕ Create New User'}
                        </div>
                        <div className="card-body">
                            <form onSubmit={handleSubmit}>
                                {/* IDENTITY */}
                                <h6 className="text-muted border-bottom pb-2 mb-3">1. Login Details</h6>
                                <div className="mb-3">
                                    <label className="form-label small fw-bold">Full Name</label>
                                    <input className="form-control" name="fullName" value={form.fullName} onChange={handleChange} required />
                                </div>
                                <div className="row g-2 mb-3">
                                    <div className="col-6">
                                        <label className="form-label small fw-bold">Username</label>
                                        <input className="form-control" name="username" value={form.username} onChange={handleChange} required disabled={!!editingId} />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label small fw-bold">Password</label>
                                        <input className="form-control" type="password" name="password" value={form.password} onChange={handleChange} placeholder={editingId ? "(Unchanged)" : "*****"} required={!editingId} />
                                    </div>
                                </div>

                                {/* NEW HOMEPAGE SELECTOR */}
                                <div className="mb-4 p-2 bg-light border rounded">
                                    <label className="form-label small fw-bold text-success">🎯 Dedicated Homepage</label>
                                    <select className="form-select fw-bold" name="homepage" value={form.homepage} onChange={handleChange}>
                                        {APP_PAGES.map(page => (
                                            <option key={page.value} value={page.value}>{page.label}</option>
                                        ))}
                                    </select>
                                    <small className="text-muted" style={{fontSize:'0.75rem'}}>User will land here immediately after login.</small>
                                </div>

                                {/* APP ACCESS */}
                                <h6 className="text-muted border-bottom pb-2 mb-3">2. App Access</h6>
                                <div className="d-flex gap-3 mb-3">
                                    <div className={`card p-2 w-50 text-center cursor-pointer border ${form.access.office ? 'bg-info text-white' : 'bg-light'}`} onClick={() => toggleAccess('office')}>
                                        🏢 Office App
                                    </div>
                                    <div className={`card p-2 w-50 text-center cursor-pointer border ${form.access.factory ? 'bg-warning text-dark' : 'bg-light'}`} onClick={() => toggleAccess('factory')}>
                                        🏭 Factory App
                                    </div>
                                </div>

                                {/* MODULE PERMISSIONS */}
                                <h6 className="text-muted border-bottom pb-2 mb-3">3. Module Permissions</h6>
                                
                                <div className="mb-2"><strong>🏭 Factory Roles</strong></div>
                                <div className="form-check form-switch">
                                    <input className="form-check-input" type="checkbox" checked={form.permissions.mfgModule} onChange={() => togglePerm('mfgModule')} />
                                    <label className="form-check-label">Auto Manager (Jobwork)</label>
                                </div>
                                <div className="form-check form-switch">
                                    <input className="form-check-input" type="checkbox" checked={form.permissions.factoryModule} onChange={() => togglePerm('factoryModule')} />
                                    <label className="form-check-label">Labor Manager (Artisans)</label>
                                </div>
                                <div className="form-check form-switch">
                                    <input className="form-check-input" type="checkbox" checked={form.permissions.galaxyModule} onChange={() => togglePerm('galaxyModule')} />
                                    <label className="form-check-label">Galaxy Manager</label>
                                </div>

                                <div className="mb-2 mt-3"><strong>💎 Office Roles</strong></div>
                                <div className="form-check form-switch">
                                    <input className="form-check-input" type="checkbox" checked={form.permissions.roughModule} onChange={() => togglePerm('roughModule')} />
                                    <label className="form-check-label">Rough / Stock Manager</label>
                                </div>
                                <div className="form-check form-switch">
                                    <input className="form-check-input" type="checkbox" checked={form.permissions.salesModule} onChange={() => togglePerm('salesModule')} />
                                    <label className="form-check-label">Sales & Trading</label>
                                </div>
                                <div className="form-check form-switch">
                                    <input className="form-check-input" type="checkbox" checked={form.permissions.financeModule} onChange={() => togglePerm('financeModule')} />
                                    <label className="form-check-label">Accountant (Finance)</label>
                                </div>
                                <div className="form-check form-switch">
                                    <input className="form-check-input" type="checkbox" checked={form.permissions.userManageModule} onChange={() => togglePerm('userManageModule')} />
                                    <label className="form-check-label text-danger fw-bold">Super Admin</label>
                                </div>

                                <button className={`btn w-100 fw-bold mt-4 ${editingId ? 'btn-warning' : 'btn-success'}`}>
                                    {editingId ? '💾 Save Changes' : '➕ Create User'}
                                </button>
                                {editingId && <button type="button" className="btn btn-secondary w-100 mt-2" onClick={resetForm}>Cancel</button>}
                            </form>
                        </div>
                    </div>
                </div>

                {/* USER LIST */}
                <div className="col-lg-7">
                    <div className="card shadow-sm">
                        <div className="card-header bg-dark text-white">Existing Users</div>
                        <div className="table-responsive">
                            <table className="table table-hover mb-0 align-middle">
                                <thead className="table-light">
                                    <tr>
                                        <th>Name</th>
                                        <th>Homepage</th>
                                        <th>Role</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map(u => (
                                        <tr key={u._id} className={u.isActive ? '' : 'table-secondary opacity-75'}>
                                            <td>
                                                <div className="fw-bold">{u.fullName}</div>
                                                <div className="small text-muted">@{u.username}</div>
                                            </td>
                                            <td>
                                                <span className="badge bg-light text-dark border">
                                                    {APP_PAGES.find(p => p.value === u.homepage)?.label || u.homepage}
                                                </span>
                                            </td>
                                            <td>
                                                {u.permissions.userManageModule 
                                                    ? <span className="badge bg-danger">Admin</span> 
                                                    : <span className="badge bg-info text-dark">Staff</span>}
                                            </td>
                                            <td>
                                                <button className="btn btn-sm btn-outline-primary" onClick={() => handleEdit(u)}>✏️ Edit</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserManagement;